import path from 'path-browserify';
import { staticData } from '@resourcehub/resourcehub-data';
import DeviceDetector from 'device-detector-js';

/**
 * Splits up an array into sub arrays of the specified
 * @param {Object[]} array what to partition
 * @param {number} size length of partiion
 * @returns {Object[]}
 */
var partitionArray = function partitionArray(array, size) {
  return array.map(function (e, i) {
    return i % size === 0 ? array.slice(i, i + size) : null;
  }).filter(function (e) {
    return e;
  });
};

var arrayServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    partitionArray: partitionArray
});

/**
 * The response is returned as HTML, this function parses into a more usable
 * format
 *
 * @param  {Object} json ajax response json
 * @param cart_checkout_url
 * @return {Object}      products, prices, & checkoutUrl
 *
 * products = [{
 *   langName: string,
 *   prodType: string,
 *   level: string,
 *   image: string
 * }, ...]
 */
var parseResponse = function parseResponse(json, cart_checkout_url) {
  var navTotalPrices = json.cartInfo; // gather cart data into navTotals

  if (navTotalPrices !== null && json.itemInfo !== null) {
    var navTotalProducts = json.itemInfo.map(function (item) {
      return {
        langName: item.language,
        prodType: item.type === "Online Sub" ? "Online Subscription" : item.type,
        level: "".concat(Number(item.sub_month), " Months"),
        image: ""
      };
    });
    var count = json.count;
    return {
      products: navTotalProducts,
      prices: navTotalPrices,
      checkoutUrl: cart_checkout_url,
      count: count
    };
  }

  return {
    products: 0,
    prices: 0,
    checkoutUrl: cart_checkout_url,
    count: 0
  };
};

var cartServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    parseResponse: parseResponse
});

// Helper function for asset_type used in Card, RelatedCard, and GatedForms (Submit Btn)

/**
 * Derives localized action for content using asset type and duration
 * @param {object} copyData Localized: 'Read Article', 'Watch Webinar', etc.
 * @param {string} asset_type 'ebook', 'guide', etc.
 * @param {string} duration '4:24', '', etc.
 * @return {string} 'Read Ebook', 'Shau Video', etc.
 */
var getAction = function getAction(copyData, asset_type, duration) {
  var actionDictionary = {
    "blog post": copyData.read_article,
    "case study": copyData.read_case_study,
    ebook: copyData.read_ebook,
    "fact sheet": copyData.read_fact_sheet,
    guide: copyData.read_guide,
    infographic: copyData.view_infographic,
    podcast: copyData.listen_now,
    report: copyData.read_report,
    video: copyData.watch_video,
    webinar: copyData.watch_webinar,
    "white paper": copyData.read_white_paper
  };

  if (duration) {
    return actionDictionary.video;
  }

  return actionDictionary[asset_type] || copyData.learn_more;
}; // Checks if Airtable text contains markup

/**
 * Constructs Support URL based on current vertical
 * @param {string} content
 * @returns {boolean} true or false
 */

var checkForMarkUp = function checkForMarkUp(content) {
  return /<[a-z][\s\S]*>/i.test(content);
}; // Converts content into dangerouslysethtml object

/**
 * Constructs Support URL based on current vertical
 * @param {string} content
 * @returns {object} __html var with content set as val to be used as dangerouslySetHTML atr
 */

var createMarkup = function createMarkup(content) {
  return {
    __html: content
  };
}; // Helper functions for constructing specified URLs

/**
 * Constructs Support URL based on current vertical
 * @param {string} vertical
 * @param {string} support_site_url
 * @returns {string} complete url
 */

var supportRouteVertical = function supportRouteVertical(vertical, support_site_url) {
  switch (vertical) {
    case "business":
      return "".concat(support_site_url, "work");

    case "k12":
    case "hed":
      return "".concat(support_site_url, "school");

    default:
      return "".concat(support_site_url, "personal");
  }
};
var createFieldClasses = function createFieldClasses(error, data) {
  var dataCheck = data === undefined ? " " : data;
  return "field__container ".concat(error ? "field__container--error" : "field__container--valid", " ").concat(dataCheck && "field__container--data");
};
/**
 * Checks if url has gated=false param, opens gate if so
 * @returns {boolean} true if URL has false param
 */

function hasGatedFalseURLParam() {
  var search = window.location.search;
  var hashes = search.slice(search.indexOf("?") + 1).split("&"); // if returns true of gated=false is present

  return hashes.includes("gated=false");
}

var componentServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getAction: getAction,
    checkForMarkUp: checkForMarkUp,
    createMarkup: createMarkup,
    supportRouteVertical: supportRouteVertical,
    createFieldClasses: createFieldClasses,
    hasGatedFalseURLParam: hasGatedFalseURLParam
});

var hasChildObjectVisible = function hasChildObjectVisible(object, property) {
  if (!object) return false;
  return Object.prototype.hasOwnProperty.call(object, property) && object[property] && object[property].visible;
};

var configurationServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hasChildObjectVisible: hasChildObjectVisible
});

/**
 *  Used to construct the create content function. Which in turn is used to build the items and streams pages.
 *
 * @param {Object} options
 * @param {string} options.hubId AirTable ID of the hub
 * @param {string} options.market market that the content is in [US, UK, etc.]
 * @param {string} options.featuredSlug slug of stream to use at root
 * @param {string} options.resourcesRoot path to use for resources root
 * @param {string} options.contentRoot path to use for content root
 * @param {boolean} [options.removeTrailingSlash] builds paths w/o trailing slash
 */

var createContentFactory = function createContentFactory(_ref) {
  var hubId = _ref.hubId,
      market = _ref.market,
      featuredSlug = _ref.featuredSlug,
      resourcesRoot = _ref.resourcesRoot,
      contentRoot = _ref.contentRoot,
      _ref$removeTrailingSl = _ref.removeTrailingSlash,
      removeTrailingSlash = _ref$removeTrailingSl === void 0 ? false : _ref$removeTrailingSl;
  return function (graphql, createPage) {
    var query = "\n  {\n    streams: allAirtable(\n      filter: {\n        table: { eq: \"Streams\" }\n        data: { \n          hubs: { eq: \"".concat(hubId, "\" }\n          market: { eq: \"").concat(market, "\" }\n        }\n      }\n    ) {\n      edges {\n        node {\n          id\n          data {\n            url_slug\n            items\n          }\n        }\n      }\n    }\n    items: allAirtable(\n      filter: {\n        table: { eq: \"Items\" }\n        data: { \n          hubs: { eq: \"").concat(hubId, "\" }\n          market: { eq: \"").concat(market, "\" }\n        }\n      }\n    ) {\n      edges {\n        node {\n          id\n          data {\n            url_slug\n            streams\n          }\n        }\n      }\n    }\n  }\n  ");
    return graphql(query).then(function (result) {
      var _result$data = result.data,
          streams = _result$data.streams,
          items = _result$data.items; // Error handling

      if (result.errors) {
        throw result.errors;
      }

      if (streams === null || items === null) {
        throw new Error("The graphQl query for streams or items failed. Please debug. AirTable?");
      }

      var trailingSlash = removeTrailingSlash ? "" : "/";

      var getItemIds = function getItemIds(itemsList) {
        return itemsList ? itemsList.map(function (rawId) {
          return "Airtable_".concat(rawId);
        }) : [];
      }; // main index pages with cards of items


      var resourcesTemplate = path.resolve("./src/templates/resources/index.jsx"); // index page/stream

      var featuredStream = streams.edges.find(function (edge) {
        return edge.node.data.url_slug === featuredSlug;
      });
      var itemIds = getItemIds(featuredStream.node.data.items);
      var rootPath = removeTrailingSlash ? resourcesRoot.replace(/\/$/, "") : resourcesRoot;
      createPage({
        path: rootPath,
        component: resourcesTemplate,
        context: {
          // id: featuredStream.node.id.split("Airtable_")[1],
          id: featuredStream.node.id,
          recordId: featuredStream.node.recordId,
          rawId: featuredStream.node.id,
          itemIds: itemIds
        }
      }); // all other streams

      streams.edges.forEach(function (edge) {
        // const id = edge.node.id.split("Airtable_")[1];
        var id = edge.node.id;
        var streamItemIds = getItemIds(edge.node.data.items);
        var streamPath = "".concat(resourcesRoot).concat(edge.node.data.url_slug).concat(trailingSlash);
        createPage({
          path: streamPath,
          component: resourcesTemplate,
          context: {
            id: id,
            recordId: edge.node.recordId,
            rawId: edge.node.id,
            itemIds: streamItemIds
          }
        });
      }); // individual details pages for content/item

      var contentTemplate = path.resolve("./src/templates/content/index.jsx");
      items.edges.forEach(function (edge) {
        // skip if no streams
        if (!edge.node.data.streams) return; // const id = edge.node.id.split("Airtable_")[1];

        var itemsId = edge.node.id;
        var itemPath = "".concat(contentRoot).concat(edge.node.data.url_slug).concat(trailingSlash);
        createPage({
          path: itemPath,
          component: contentTemplate,
          context: {
            id: itemsId,
            recordId: edge.node.recordId,
            rawId: edge.node.id,
            streamId: edge.node.data.streams[0]
          }
        });
      });
    });
  };
};

var contentServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createContentFactory: createContentFactory
});

/**
 * Validates an email using regex
 *
 * @param {String} email An email to validate
 */
var validateEmail = function validateEmail(email) {
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
};
var validateFreetrialPassword = function validateFreetrialPassword(passwd) {
  return !(passwd.trim().length < 6 || !/^[^,|"]*$/gi.test(passwd));
};

var emailServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    validateEmail: validateEmail,
    validateFreetrialPassword: validateFreetrialPassword
});

var filterData = staticData.filterData;
/**
 * Selects the appropriate data to build filter bar based on provided vertical
 *
 * @param market
 * @param {string} vertical ['business', 'k12', services']
 * @returns {Object} data used to build filter
 */

var getFilterBarData = function getFilterBarData(market, vertical) {
  var formattedMarket = market.toString().toLowerCase();
  var hub = "".concat(formattedMarket, "-").concat(vertical);

  switch (hub) {
    case "us-business":
      return filterData.businessData;

    case "us-k12":
      return filterData.k12Data;

    case "uk-business":
      return filterData.ukBusinessData;

    case "de-business":
      return filterData.deBusinessData;

    case "de-services":
      return filterData.deServicesData;

    case "la-business":
      return filterData.laBusinessData;

    case "es-business":
      return filterData.esBusinessData;

    case "es-k12":
      return filterData.esK12Data;

    case "es-hed":
      return filterData.esHEDData;

    case "fr-business":
      return filterData.frBusinessData;

    case "br-business":
      return filterData.brBusinessData;

    case "it-business":
      return filterData.itBusinessData;

    case "cn-business":
    case "cn-k12":
    case "cn-hed":
      return filterData.cnBusinessData;

    default:
      return "";
  }
};

var filterServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getFilterBarData: getFilterBarData
});

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var languageFormatEfigs = function languageFormatEfigs(copyData) {
  return [{
    url: copyData.learn_spanish_url,
    title: copyData.learn_spanish,
    small: copyData.latin_america
  }, {
    url: copyData.learn_french_url,
    title: copyData.learn_french,
    small: ""
  }, {
    url: copyData.learn_italian_url,
    title: copyData.learn_italian,
    small: ""
  }, {
    url: copyData.learn_german_url,
    title: copyData.learn_german,
    small: ""
  }, {
    url: copyData.learn_english_url,
    title: copyData.learn_english,
    small: copyData.american
  }];
};
var languageFormatOther = function languageFormatOther(copyData) {
  var languageList = [];

  if (copyData.learn_arabic_url && copyData.arabic) {
    languageList.push({
      url: copyData.learn_arabic_url,
      title: copyData.arabic,
      small: ""
    });
  }

  if (copyData.learn_chinese_url && copyData.learn_chinese) {
    languageList.push({
      url: copyData.learn_chinese_url,
      title: copyData.learn_chinese,
      small: copyData.madarin
    });
  }

  if (copyData.learn_dutch_url && copyData.dutch) {
    languageList.push({
      url: copyData.learn_dutch_url,
      title: copyData.dutch,
      small: ""
    });
  }

  if (copyData.learn_english_british_url && copyData.english) {
    languageList.push({
      url: copyData.learn_english_british_url,
      title: copyData.english,
      small: copyData.british
    });
  }

  if (copyData.learn_tagalog_url && copyData.filipino) {
    languageList.push({
      url: copyData.learn_tagalog_url,
      title: copyData.filipino,
      small: copyData.tagalog
    });
  }

  if (copyData.learn_greek_url && copyData.greek) {
    languageList.push({
      url: copyData.learn_greek_url,
      title: copyData.greek,
      small: ""
    });
  }

  if (copyData.learn_hebrew_url && copyData.hebrew) {
    languageList.push({
      url: copyData.learn_hebrew_url,
      title: copyData.hebrew,
      small: ""
    });
  }

  if (copyData.learn_hindi_url && copyData.hindi) {
    languageList.push({
      url: copyData.learn_hindi_url,
      title: copyData.hindi,
      small: ""
    });
  }

  if (copyData.learn_irish_url && copyData.irish) {
    languageList.push({
      url: copyData.learn_irish_url,
      title: copyData.irish,
      small: ""
    });
  }

  if (copyData.learn_japanese_url && copyData.japanese) {
    languageList.push({
      url: copyData.learn_japanese_url,
      title: copyData.japanese,
      small: ""
    });
  }

  if (copyData.learn_korean_url && copyData.korean) {
    languageList.push({
      url: copyData.learn_korean_url,
      title: copyData.korean,
      small: ""
    });
  }

  if (copyData.learn_latin_url && copyData.latin) {
    languageList.push({
      url: copyData.learn_latin_url,
      title: copyData.latin,
      small: ""
    });
  }

  if (copyData.learn_persian_url && copyData.persian) {
    languageList.push({
      url: copyData.learn_persian_url,
      title: copyData.persian,
      small: copyData.farsi
    });
  }

  if (copyData.learn_polish_url && copyData.polish) {
    languageList.push({
      url: copyData.learn_polish_url,
      title: copyData.polish,
      small: ""
    });
  }

  if (copyData.learn_portuguese_url && copyData.portuguese) {
    languageList.push({
      url: copyData.learn_portuguese_url,
      title: copyData.portuguese,
      small: copyData.brazil
    });
  }

  if (copyData.learn_russian_url && copyData.russian) {
    languageList.push({
      url: copyData.learn_russian_url,
      title: copyData.russian,
      small: ""
    });
  }

  if (copyData.learn_spanish_spain_url && copyData.spanish) {
    languageList.push({
      url: copyData.learn_spanish_spain_url,
      title: copyData.spanish,
      small: copyData.spain
    });
  }

  if (copyData.learn_swedish_url && copyData.swedish) {
    languageList.push({
      url: copyData.learn_swedish_url,
      title: copyData.swedish,
      small: ""
    });
  }

  if (copyData.learn_turkish_url && copyData.turkish) {
    languageList.push({
      url: copyData.learn_turkish_url,
      title: copyData.turkish,
      small: ""
    });
  }

  if (copyData.learn_vietnamese_url && copyData.vietnamese) {
    languageList.push({
      url: copyData.learn_vietnamese_url,
      title: copyData.vietnamese,
      small: ""
    });
  }

  return languageList;
};
var languageFormatMobile = function languageFormatMobile(copyData) {
  var efigs = languageFormatEfigs(copyData);
  var other = languageFormatOther(copyData);
  return efigs.concat(other).map(function (l) {
    return _objectSpread2(_objectSpread2({}, l), {}, {
      title: l.title.replace("Learn ", "")
    });
  });
};

var languageServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    languageFormatEfigs: languageFormatEfigs,
    languageFormatOther: languageFormatOther,
    languageFormatMobile: languageFormatMobile
});

var resourcesTitleSort = function resourcesTitleSort(a, b) {
  if (a) return 0;
  var nameA = a.node.data.title.toUpperCase(); // ignore upper and lowercase

  var nameB = b.node.data.title.toUpperCase(); // ignore upper and lowercase

  if (nameA < nameB) {
    return -1;
  }

  if (nameA > nameB) {
    return 1;
  }

  return 0;
};
/**
 * Trims long titles so they fit on two lines. Long titles show up with an elipsis, shorter titles are returned unedited.
 * @param {string} title of Card
 * @return {string} truncated title
 */

var resourcesTrimTitle = function resourcesTrimTitle(title) {
  // Max length for title to fit on two lines, prior to ellipse concat
  var maxLength = 63; // Sets max length on title

  var trimmedTitle = title.substring(0, maxLength); // If title is greater than max length, return, else return original title

  return title.length > maxLength ? trimmedTitle // Returns string with last full word (if word after space, returns last space before additional / half words)
  .substr(0, Math.min(trimmedTitle.length, trimmedTitle.lastIndexOf(" "))).concat("...") : title;
};

var resourcesServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    resourcesTitleSort: resourcesTitleSort,
    resourcesTrimTitle: resourcesTrimTitle
});

var calcAnchorNavWidth = function calcAnchorNavWidth(has_resources, marketString, isMobile) {
  // if in mobile, make container 100% and each link 100%
  if (isMobile) {
    return "100%";
  } // US site needs to be smaller so it accommodates search and cart items


  if (has_resources && marketString !== "DE") {
    return "130px";
  } // These instances have no search or cart, thus need to be bigger


  return "170px";
};
var preventDefault = function preventDefault(e) {
  if (e.preventDefault) e.preventDefault();
  e.returnValue = false;
};
var disableScroll = function disableScroll() {
  document.body.style.height = "100%";
  document.body.style.overflow = "hidden";
  window.ontouchmove = preventDefault;
};
var enableScroll = function enableScroll() {
  document.body.style.height = "";
  document.body.style.overflow = "";
  window.ontouchmove = null;
};
var navigateTo = function navigateTo(destination) {
  var new_window = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  if (new_window) {
    var a = document.createElement("a");
    a.href = destination;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  } else {
    window.location.href = destination;
  }
};
var queryStringToJson = function queryStringToJson(search) {
  var pairs = search.slice(1).split("&");
  var result = {};
  pairs.forEach(function (pair) {
    pair = pair.split("=");
    result[pair[0]] = decodeURIComponent(pair[1] || "");
  });
  return JSON.parse(JSON.stringify(result));
};
var addTrailingSlash = function addTrailingSlash(url) {
  if (url.endsWith("/")) {
    return url;
  }

  return url + "/";
};

var siteServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    calcAnchorNavWidth: calcAnchorNavWidth,
    preventDefault: preventDefault,
    disableScroll: disableScroll,
    enableScroll: enableScroll,
    navigateTo: navigateTo,
    queryStringToJson: queryStringToJson,
    addTrailingSlash: addTrailingSlash
});

var buildSliderData = function buildSliderData(data) {
  return [{
    phrase: "Una bicicleta azul",
    leftImg_src: data.bicicletaAzul,
    leftImg_correct: true,
    rightImg_src: data.bicicletaAmarilla,
    rightImg_correct: false
  }, {
    phrase: "Dos bicicletas azules",
    leftImg_src: data.dosBicicletasAzules,
    leftImg_correct: true,
    rightImg_src: data.dosBicicletasDiferentes,
    rightImg_correct: false
  }, {
    phrase: "Dos bicicletas azules por un día por favor.",
    leftImg_src: data.verdesPorUnDia,
    leftImg_correct: false,
    rightImg_src: data.azulesPorUnDia,
    rightImg_correct: true
  }];
};

var sliderServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    buildSliderData: buildSliderData
});

var deriveLinks = function deriveLinks(vertical, copyData, combinedVerticals, data) {
  if (combinedVerticals) {
    return {
      linkOne: copyData.vertical_k12_link,
      textOne: copyData.vertical_k12,
      imageOne: data.k12_circle.childImageSharp.fixed,
      linkTwo: copyData.vertical_hed_link,
      textTwo: copyData.vertical_hed,
      imageTwo: data.hed_circle.childImageSharp.fixed,
      linkThree: copyData.vertical_business_link,
      textThree: copyData.vertical_business,
      imageThree: data.business_circle.childImageSharp.fixed
    };
  }

  if (copyData.market.toString() === "LA") {
    return {
      linkOne: copyData.vertical_business_link,
      textOne: copyData.vertical_business,
      imageOne: data.business_circle.childImageSharp.fixed,
      linkTwo: copyData.vertical_k12_link,
      textTwo: copyData.vertical_k12,
      imageTwo: data.k12_circle.childImageSharp.fixed,
      linkThree: copyData.vertical_hed_link,
      textThree: copyData.vertical_hed,
      imageThree: data.hed_circle.childImageSharp.fixed
    };
  }

  if (copyData.market.toString() === "BR") {
    return {
      linkOne: copyData.vertical_business_link,
      textOne: copyData.vertical_business,
      imageOne: data.business_circle.childImageSharp.fixed,
      linkTwo: copyData.vertical_k12_link,
      textTwo: copyData.vertical_k12,
      imageTwo: data.k12_circle.childImageSharp.fixed,
      linkThree: copyData.vertical_hed_link,
      textThree: copyData.vertical_hed,
      imageThree: data.hed_circle.childImageSharp.fixed
    };
  }

  switch (vertical) {
    case "k12":
      return {
        linkOne: copyData.vertical_pub_link,
        textOne: copyData.vertical_pub,
        imageOne: data.pub_circle.childImageSharp.fixed,
        linkTwo: copyData.vertical_business_link,
        textTwo: copyData.vertical_business,
        imageTwo: data.business_circle.childImageSharp.fixed,
        linkThree: copyData.vertical_hed_link,
        textThree: copyData.vertical_hed,
        imageThree: data.hed_circle.childImageSharp.fixed
      };

    case "hed":
      return {
        linkOne: copyData.vertical_pub_link,
        textOne: copyData.vertical_pub,
        imageOne: data.pub_circle.childImageSharp.fixed,
        linkTwo: copyData.vertical_business_link,
        textTwo: copyData.vertical_business,
        imageTwo: data.business_circle.childImageSharp.fixed,
        linkThree: copyData.vertical_k12_link,
        textThree: copyData.vertical_k12,
        imageThree: data.k12_circle.childImageSharp.fixed
      };

    case "pubsec":
      return {
        linkOne: copyData.vertical_business_link,
        textOne: copyData.vertical_business,
        imageOne: data.business_circle.childImageSharp.fixed,
        linkTwo: copyData.vertical_k12_link,
        textTwo: copyData.vertical_k12,
        imageTwo: data.k12_circle.childImageSharp.fixed,
        linkThree: copyData.vertical_hed_link,
        textThree: copyData.vertical_hed,
        imageThree: data.hed_circle.childImageSharp.fixed
      };

    default:
      return {
        linkOne: copyData.vertical_pub_link,
        textOne: copyData.vertical_pub,
        imageOne: data.pub_circle.childImageSharp.fixed,
        linkTwo: copyData.vertical_k12_link,
        textTwo: copyData.vertical_k12,
        imageTwo: data.k12_circle.childImageSharp.fixed,
        linkThree: copyData.vertical_hed_link,
        textThree: copyData.vertical_hed,
        imageThree: data.hed_circle.childImageSharp.fixed
      };
  }
}; // Helper functions for E&E sites that span multiple verticals i.e. China (CN)

/**
 * Derives the route needed for each vertical based on the market. Returns
 * empty string if not needed.
 * @param {string|string[]} market ['CN', 'US', 'UK', etc.]
 * @param {string} vertical ['business', 'k12', etc.]
 * @return {string}
 */

var deriveRouteForVertical = function deriveRouteForVertical(market, vertical) {
  // Market is array, set as string
  var marketString = typeof market !== "string" ? market.toString() : market;

  if (marketString === "CN") {
    // China pages instances, can add others in future
    switch (vertical) {
      case "business":
        return "/business";

      case "k12":
        return "/training-centers";

      case "hed":
        return "/education";

      default:
        return "";
    }
  }

  return "";
};
/**
 * Computes the route prefix needed for sites that span multiple verticals and
 * have a single resource hub. Based on the market
 * @param {string|string[]} market
 * @return {string}
 */

var getHubPrefixFromVertical = function getHubPrefixFromVertical(market) {
  // Market is array, set as string
  var marketString = typeof market !== "string" ? market.toString() : market;

  if (marketString === "CN") {
    return "/business";
  }

  return "";
};
/**
 * Returns bool based on whether the market has the verticals sites combined
 * into a single app
 * @param {string|string[]} market ['CN', 'US', 'UK', etc.]
 * @return {boolean}
 */

var isCombinedVertical = function isCombinedVertical(market) {
  return market.toString() === "CN";
};
/**
 * Returns string from siteMetadata URL and localized business word
 * @param {string} siteUrl 'https://www.rosettastone.de'
 * @param {string} businessLocalized 'unternehmen'
 * @return {string}
 */

var buildAbsolutePath = function buildAbsolutePath(siteUrl, businessLocalized) {
  return "".concat(siteUrl, "/").concat(businessLocalized);
};

var verticalsServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    deriveLinks: deriveLinks,
    deriveRouteForVertical: deriveRouteForVertical,
    getHubPrefixFromVertical: getHubPrefixFromVertical,
    isCombinedVertical: isCombinedVertical,
    buildAbsolutePath: buildAbsolutePath
});

function isString(s) {
  return typeof s === "string" || s instanceof String;
} // we want all logic in this file -- even these one liners
// because they could change and if we are sharing this file
// across projects we could break code


function isUnlimitedF(level, languages) {
  return /-u/.test(level) || languages === "unlimited";
}
function productRibbonTextF(ribbonText) {
  return isString(ribbonText) ? ribbonText : ribbonText && ribbonText.text;
}
function xpayQualifiedF(xpay, price) {
  return !!(xpay && price >= 100);
}
function showMsrpF(productMsrp, price) {
  return productMsrp !== price;
}
function finalPriceF(productXpay, price, computedPrice) {
  return productXpay ? price : computedPrice;
}
function computeRibbonText(ribbonText, overrideRibbonText, level) {
  if (overrideRibbonText) {
    try {
      // TODO document the format of overrideRibbonText (url param)
      // Remove "-u" from level, if there.
      var _level = _level.replace("-u", "");

      var ribbontextObj = JSON.parse(overrideRibbonText.replace(/_/g, '"'));
      var textValue = ribbontextObj[_level];

      if (textValue) {
        ribbonText = textValue;
      }
    } catch (e) {
      console.warn("Could not parse ribbon text parameter.");
      console.log(e);
    }
  }

  return ribbonText;
}
function orderProducts(productList, productFilter, langId) {
  var filtered = productList.filter(function (p) {
    return p.id === langId && productFilter.includes(p.lvl);
  }); // We want the order to be non-lifetime..., lifetime, lifetime-hs, lifetime-u, lifetimeplus, lifetimeplus-hs, lifetimeplus-u

  var lifetime = filtered.filter(function (p) {
    return /lifetime/.test(p.lvl) && !/lifetime-/.test(p.lvl) && !/lifetimeplus/.test(p.lvl);
  });
  var lifetimeHS = filtered.filter(function (p) {
    return /lifetime-hs/.test(p.lvl);
  });
  var lifetimeU = filtered.filter(function (p) {
    return /lifetime-u/.test(p.lvl);
  });
  var lifetimePlus = filtered.filter(function (p) {
    return /lifetimeplus/.test(p.lvl) && !/lifetimeplus-u/.test(p.lvl) && !/lifetimeplus-hs/.test(p.lvl);
  });
  var lifetimePlusHS = filtered.filter(function (p) {
    return /lifetimeplus-hs/.test(p.lvl);
  });
  var lifetimePlusU = filtered.filter(function (p) {
    return /lifetimeplus-u/.test(p.lvl);
  }); // we pull lifetime and lifetimeplus products out of the array since they won't sort the way we want

  filtered = filtered.filter(function (p) {
    return !/lifetime/.test(p.lvl);
  }); // parseInt to convert "12-hs" or "6-u" into just the int value so we can sort

  filtered = filtered.sort(function (a, b) {
    return parseInt(a.lvl) < parseInt(b.lvl) ? -1 : 1;
  }); // add back the lifetime and lifetimeplus products in the desired order

  return filtered.concat(lifetime).concat(lifetimeHS).concat(lifetimeU).concat(lifetimePlus).concat(lifetimePlusHS).concat(lifetimePlusU);
} // TODO i'm thinking we want to merge this logic into computeFeaturedPriceF

function dueTextF(productShowFullPrice, xpayQualified, level, perMonth, due) {
  if (!productShowFullPrice && !xpayQualified && !/lifetime/.test(level)) {
    return perMonth;
  } else if (productShowFullPrice) {
    return "";
  }

  return due;
}
function computePercentageF(productMsrp, price) {
  return Math.floor((productMsrp - price) / productMsrp * 100);
}
function computedPriceF(priceArg, productXpay) {
  // Makes it so cents are 2 digits - only if there are cents.
  // So instead of 199.5, it'll show 199.50
  var price = priceArg.toString().indexOf(".") !== -1 ? priceArg.toLocaleString("en", {
    minimumFractionDigits: 2
  }) : priceArg.toString(); // note: If arg to parseFloat is not a string, then it is converted to one using ToString

  if (productXpay) {
    price = (Math.ceil(parseFloat(priceArg) / parseInt(productXpay, 10) * 100) / 100).toFixed(2).split(".");
    price = price[1] === "00" ? price[0] : price.join(".");
  }

  return price;
}
function computeFeaturedPriceF(price, productShowFullPrice, computedPrice, productXPay, level) {
  // What we'll be dividing prices by to calculate monthly. use level by default
  var divideBy = level;

  if (productXPay) {
    productShowFullPrice = false;
    divideBy = productXPay;
  } // Can't have monthly price for lifetime products.


  if (/lifetime/gi.test(level)) {
    productShowFullPrice = true;
    divideBy = "1";
  } // Determine our monthly price: price/level


  var monthly = (Math.ceil(parseFloat(price) / parseInt(divideBy, 10) * 100) / 100).toFixed(2).split("."); // Set our dollars and cents for html view

  var dollars = monthly[0];
  var cents = monthly[1] === undefined ? "00" : monthly[1];
  cents = cents === "00" ? "" : cents; // If showFullPrice is true, don't show monthly pricing

  if (productShowFullPrice) {
    dollars = computedPrice.toString().split(".")[0];
    cents = computedPrice.toString().split(".")[1] || "";
  }

  return {
    dollars: dollars,
    cents: cents,
    productShowFullPrice: productShowFullPrice
  };
}
function cartLinkF(cartLink, productXpay, email, licenseGuid, cid) {
  var url = new URL(cartLink);
  url.pathname = url.pathname.replace(/\/+$/, ""); // remove slashes on the end

  var searchParams = new URLSearchParams(url.search);

  if (productXpay) {
    // Format cart link. A normal cart link looks like this:
    // https://secure.rosettastone.com/us_en_store_view/checkout/cart/add/sku/90291/category_id/esp/
    // With xpay, We make it look like this:
    // https://secure.rosettastone.com/us_en_store_view/checkout/cart/add/sku/90291/category_id/esp/pp/3/
    url.pathname += "/pp/" + productXpay;
  }

  if (email) {
    searchParams.append("email", email);
  }

  if (licenseGuid) {
    searchParams.append("licenseGUID", licenseGuid);
  }

  if (cid) {
    searchParams.append("cid", cid);
  }

  url.search = searchParams.toString();
  return url.toString();
}

var productBoxServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    isUnlimitedF: isUnlimitedF,
    productRibbonTextF: productRibbonTextF,
    xpayQualifiedF: xpayQualifiedF,
    showMsrpF: showMsrpF,
    finalPriceF: finalPriceF,
    computeRibbonText: computeRibbonText,
    orderProducts: orderProducts,
    dueTextF: dueTextF,
    computePercentageF: computePercentageF,
    computedPriceF: computedPriceF,
    computeFeaturedPriceF: computeFeaturedPriceF,
    cartLinkF: cartLinkF
});

/**
 * Uses browser to detect user device.
 * Returns one of the following:
 * desktop
 * tablet
 * phone
 */

var getUserDeviceStorybook = function getUserDeviceStorybook() {
  // Prevents errors on the dev side on hard reloadd.
  // Server doesnt't have a window object
  if (typeof window === "undefined") {
    return;
  }

  var device;

  if (/(up.browser|up.link|mmp|symbian|smartphone|midp|wap|phone|android|iemobile|w3c|acs\-|alav|alca|amoi|audi|avan|benq|bird|blac|blaz|brew|cell|cldc|cmd\-|dang|doco|eric|hipt|inno|ipaq|java|jigs|kddi|keji|leno|lg\-c|lg\-d|lg\-g|lge\-|maui|maxo|midp|mits|mmef|mobi|mot\-|moto|mwbp|nec\-|newt|noki|palm|pana|pant|phil|play|port|prox|qwap|sage|sams|sany|sch\-|sec\-|send|seri|sgh\-|shar|sie\-|siem|smal|smar|sony|sph\-|symb|t\-mo|teli|tim\-|tosh|tsm\-|upg1|upsi|vk\-v|voda|wap\-|wapa|wapi|wapp|wapr|webc|winw|winw|xda|xda\-) /i.test(navigator.userAgent)) {
    if (/(tablet|ipad|playbook)|(android(?!.*(mobi|opera mini)))/i.test(navigator.userAgent)) {
      device = "tablet";
    } else {
      device = "phone";
    }
  } else if (/(tablet|ipad|playbook)|(android(?!.*(mobi|opera mini)))/i.test(navigator.userAgent)) {
    device = "tablet";
  } else {
    device = "desktop";
  }

  return device;
};
var getUserDevice = function getUserDevice() {
  var deviceDetector = new DeviceDetector();

  var _deviceDetector$parse = deviceDetector.parse(navigator.userAgent),
      device = _deviceDetector$parse.device;

  return device.type;
};

var userDeviceServices = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getUserDeviceStorybook: getUserDeviceStorybook,
    getUserDevice: getUserDevice
});

export { arrayServices, cartServices, componentServices, configurationServices as configServices, contentServices, emailServices, filterServices, languageServices, productBoxServices, resourcesServices as resourceServices, siteServices, sliderServices, userDeviceServices, verticalsServices as verticalServices };
